import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { ProfileProvider, useProfile } from "@/context/ProfileContext";
import { Header, pathwayEligible } from "@/components/Header";
import { TutorWidget } from "@/components/TutorWidget";
import { ChatBubble } from "@/components/messages/ChatBubble";
import { DailyCheckIn } from "@/components/buddy/DailyCheckIn";
import { RequireRole, Loading, homeFor } from "@/components/RequireRole";
import { SeatTimeTracker } from "@/lib/seatTime";
import AdminLeads from "@/pages/admin/AdminLeads";
import Portfolio from "@/pages/records/Portfolio";
import ReportCard from "@/pages/records/ReportCard";
import Transcript from "@/pages/records/Transcript";
import EnrollmentLetter from "@/pages/records/EnrollmentLetter";
import Diploma from "@/pages/records/Diploma";
import Accommodations from "@/pages/records/Accommodations";
import Library from "@/pages/admin/Library";
import LiveAdmin from "@/pages/admin/LiveAdmin";
import LiveClasses from "@/pages/LiveClasses";
import LiveRoom from "@/pages/LiveRoom";
import MemoryBookPage from "@/pages/MemoryBookPage";
import SchoolChat from "@/pages/SchoolChat";
import Planner from "@/pages/Planner";
import MyWords from "@/pages/MyWords";
import PracticeList from "@/pages/records/PracticeList";
import CourseWorkPage from "@/pages/records/CourseWorkPage";
import KidLogin from "@/pages/KidLogin";
import CheckIn from "@/pages/CheckIn";
import LevelUp from "@/pages/LevelUp";
import GrowthReport from "@/pages/records/GrowthReport";
import { CelebrationProvider } from "@/components/Celebration";
import LoginCards from "@/pages/parent/LoginCards";
import Messages from "@/pages/Messages";
import Legal from "@/pages/public/Legal";
import Billing from "@/pages/Billing";
import DaycareDashboard from "@/pages/daycare/DaycareDashboard";
import { GiftPage, GiftThanks, RedeemPage } from "@/pages/Gift";
import { ForgotPassword, ResetPassword } from "@/pages/PasswordReset";
import { Footer } from "@/components/Footer";
import ParentDashboard from "@/pages/parent/ParentDashboard";
import { NotificationsProvider, ReminderBanner } from "@/components/Notifications";
import { ClassPopup } from "@/components/school/ClassPopup";
import { SpanishTap } from "@/components/SpanishTap";
import DocEditor from "@/pages/admin/DocEditor";
import CurriculumLibrary from "@/pages/public/CurriculumLibrary";
import CurriculumDoc from "@/pages/public/CurriculumDoc";
import Pricing from "@/pages/Pricing";
import { PaymentSuccess, PaymentCancel } from "@/pages/Payment";
import { CourseList, CourseDocs } from "@/pages/admin/Courses";
import Home from "@/pages/Home";
import SubjectPage from "@/pages/SubjectPage";
import LessonFlow from "@/pages/LessonFlow";
import Journal from "@/pages/Journal";
import BuddyPage from "@/pages/Buddy";
import PathwayHome from "@/pages/pathway/PathwayHome";
import PathwayLesson from "@/pages/pathway/PathwayLesson";
import GedSubject from "@/pages/pathway/GedSubject";
import BusinessPlan from "@/pages/pathway/BusinessPlan";
import Certificate from "@/pages/pathway/Certificate";
import GedExam from "@/pages/pathway/GedExam";
import PitchPractice from "@/pages/pathway/PitchPractice";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import TeacherDashboard from "@/pages/teacher/TeacherDashboard";
import ClassDetail from "@/pages/teacher/ClassDetail";
import StudentDetail from "@/pages/teacher/StudentDetail";

const HomeGate = () => {
  const { profile } = useProfile();
  if (profile === null) return <Loading />;
  if (profile && profile.role !== "student") return <Navigate to={homeFor(profile.role)} replace />;
  return <Home />;
};

const GuestOnly = ({ children }) => {
  const { profile } = useProfile();
  if (profile === null) return <Loading />;
  if (profile) return <Navigate to={homeFor(profile.role, profile.learner_type)} replace />;
  return children;
};

const StudentTutor = () => {
  const { profile } = useProfile();
  return <><ChatBubble key={`chat-${profile?.id}`} />{profile?.role === "student" && <><TutorWidget key={profile.id} /><DailyCheckIn key={`ci-${profile.id}`} /></>}</>;
};

const BillingGate = () => { const { profile } = useProfile(); return profile.role === "student" && profile.learner_type !== "adult" ? <Navigate to="/" replace /> : <Billing />; };
const student = (el) => <RequireRole role="student">{el}</RequireRole>;
const PathwayGate = ({ children }) => { const { profile } = useProfile(); return pathwayEligible(profile) ? children : <Navigate to="/" replace />; };
const pathway = (el) => student(<PathwayGate>{el}</PathwayGate>);
const teacher = (el) => <RequireRole role="teacher">{el}</RequireRole>;
const viewer = (el) => <RequireRole role={["student", "teacher", "admin", "parent"]}>{el}</RequireRole>;

function App() {
  return (
    <div className="App grain min-h-screen">
      <BrowserRouter>
        <ProfileProvider>
          <NotificationsProvider>
          <CelebrationProvider>
          <Header />
          <ReminderBanner />
          <ClassPopup />
          <SpanishTap />
          <main className="pb-28 sm:pb-24">
            <Routes>
              <Route path="/" element={<HomeGate />} />
              <Route path="/login" element={<GuestOnly><Login /></GuestOnly>} />
              <Route path="/kids" element={<GuestOnly><KidLogin /></GuestOnly>} />
              <Route path="/register" element={<GuestOnly><Register /></GuestOnly>} />
              <Route path="/subject/:subjectId" element={student(<SubjectPage />)} />
              <Route path="/learn/:subjectId" element={student(<LessonFlow />)} />
              <Route path="/journal" element={student(<Journal />)} />
              <Route path="/buddy" element={student(<BuddyPage />)} />
              <Route path="/pathway" element={pathway(<PathwayHome />)} />
              <Route path="/pathway/ged/:subjectId" element={pathway(<GedSubject />)} />
              <Route path="/pathway/lesson/:track/:subjectId/:unit" element={pathway(<PathwayLesson />)} />
              <Route path="/pathway/plan" element={pathway(<BusinessPlan />)} />
              <Route path="/pathway/exam/:examId" element={pathway(<GedExam />)} />
              <Route path="/pathway/pitch" element={pathway(<PitchPractice />)} />
              <Route path="/certificate/:certId" element={<Certificate />} />
              <Route path="/teacher" element={teacher(<TeacherDashboard />)} />
              <Route path="/teacher/classes/:classId" element={teacher(<ClassDetail />)} />
              <Route path="/teacher/classes/:classId/students/:studentId" element={teacher(<StudentDetail />)} />
              <Route path="/admin" element={<RequireRole role="admin"><AdminLeads /></RequireRole>} />
              <Route path="/admin/library" element={<RequireRole role="admin"><Library /></RequireRole>} />
              <Route path="/admin/live" element={<RequireRole role="admin"><LiveAdmin /></RequireRole>} />
              <Route path="/live" element={student(<LiveClasses />)} />
              <Route path="/live/:classId" element={student(<LiveRoom />)} />
              <Route path="/live/:classId/memory" element={student(<MemoryBookPage />)} />
              <Route path="/chat" element={student(<SchoolChat />)} />
              <Route path="/planner" element={student(<Planner />)} />
              <Route path="/words" element={student(<MyWords />)} />
              <Route path="/practice/:studentId" element={viewer(<PracticeList />)} />
              <Route path="/coursework/:studentId" element={viewer(<CourseWorkPage />)} />
              <Route path="/check-in" element={student(<CheckIn />)} />
              <Route path="/level-up/:subjectId" element={student(<LevelUp />)} />
              <Route path="/growth/:studentId" element={viewer(<GrowthReport />)} />
              <Route path="/planner/:studentId" element={<RequireRole role={["teacher", "parent", "admin"]}><Planner /></RequireRole>} />
              <Route path="/parent" element={<RequireRole role="parent"><ParentDashboard /></RequireRole>} />
              <Route path="/daycare" element={<RequireRole role="daycare"><DaycareDashboard /></RequireRole>} />
              <Route path="/messages" element={<RequireRole role={["student", "teacher", "parent"]}><Messages /></RequireRole>} />
              <Route path="/parent/login-cards" element={<RequireRole role="parent"><LoginCards /></RequireRole>} />
              <Route path="/parent/kids/:kidId/memory/:classId" element={<RequireRole role="parent"><MemoryBookPage /></RequireRole>} />
              <Route path="/admin/library/:docKey" element={<RequireRole role="admin"><DocEditor /></RequireRole>} />
              <Route path="/curriculum" element={<CurriculumLibrary />} />
              <Route path="/pricing" element={<Pricing />} />
              <Route path="/gift" element={<GiftPage />} />
              <Route path="/gift/thanks" element={<GiftThanks />} />
              <Route path="/redeem" element={<RedeemPage />} />
              <Route path="/privacy" element={<Legal doc="privacy" />} />
              <Route path="/terms" element={<Legal doc="terms" />} />
              <Route path="/refunds" element={<Legal doc="refunds" />} />
              <Route path="/forgot-password" element={<GuestOnly><ForgotPassword /></GuestOnly>} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/billing" element={<RequireRole role={["teacher", "admin", "parent", "student", "daycare"]}><BillingGate /></RequireRole>} />
              <Route path="/payment/success" element={<PaymentSuccess />} />
              <Route path="/payment/cancel" element={<PaymentCancel />} />
              <Route path="/admin/courses" element={<RequireRole role="admin"><CourseList /></RequireRole>} />
              <Route path="/admin/courses/:state/:courseId" element={<RequireRole role="admin"><CourseDocs /></RequireRole>} />
              <Route path="/curriculum/:docKey" element={<CurriculumDoc />} />
              <Route path="/portfolio/:studentId" element={viewer(<Portfolio />)} />
              <Route path="/report/:studentId" element={viewer(<ReportCard />)} />
              <Route path="/transcript/:studentId" element={viewer(<Transcript />)} />
              <Route path="/enrollment/:studentId" element={viewer(<EnrollmentLetter />)} />
              <Route path="/diploma/:studentId" element={viewer(<Diploma />)} />
              <Route path="/accommodations/:studentId" element={viewer(<Accommodations />)} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </main>
          <Footer />
          <StudentTutor />
          <SeatTimeTracker />
          <Toaster position="top-center" richColors />
          </CelebrationProvider>
          </NotificationsProvider>
        </ProfileProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
